import { useMemo, useCallback } from "react";
import type { Feature } from "./featureTypes";
import { computeFlatPattern, flatPatternToDxf } from "./sheetMetalUtils";

interface BendTablePanelProps {
  features: Feature[];
  onShowFlatPattern?: (script: string) => void;
}

export default function BendTablePanel({ features, onShowFlatPattern }: BendTablePanelProps) {
  const pattern = useMemo(() => computeFlatPattern(features), [features]);

  const handleExportDxf = useCallback(() => {
    if (!pattern) return;
    const dxf = flatPatternToDxf(pattern);
    const blob = new Blob([dxf], { type: "application/dxf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flat_pattern.dxf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [pattern]);

  if (!pattern) {
    return (
      <div className="bend-table-empty">
        <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.8" width="28" height="28" opacity="0.25">
          <path d="M4 24h24v2H4zM4 24V8h24v16"/>
          <path d="M10 24V14" strokeDasharray="2 2"/>
          <path d="M22 24V14" strokeDasharray="2 2"/>
        </svg>
        <span>No sheet metal features</span>
        <span className="bend-table-hint">Add a sheet metal base to see the bend table</span>
      </div>
    );
  }

  return (
    <div className="bend-table-panel">
      <div className="bend-table-header">
        <span>Flat Pattern</span>
        <div className="bend-table-actions">
          {onShowFlatPattern && (
            <button className="bend-table-action-btn" onClick={() => onShowFlatPattern(pattern.flatScript)} title="Preview flat pattern">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                <rect x="2" y="4" width="12" height="8" rx="0.5"/>
              </svg>
              Preview
            </button>
          )}
          <button className="bend-table-action-btn" onClick={handleExportDxf} title="Export flat pattern as DXF">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
              <path d="M8 2v8M5 7l3 3 3-3M3 12h10"/>
            </svg>
            DXF
          </button>
        </div>
      </div>

      <div className="bend-table-dims">
        <div className="bend-table-dim">
          <span className="bend-table-dim-label">Width</span>
          <span className="bend-table-dim-value">{pattern.totalWidth} mm</span>
        </div>
        <div className="bend-table-dim">
          <span className="bend-table-dim-label">Length</span>
          <span className="bend-table-dim-value">{pattern.totalLength} mm</span>
        </div>
        <div className="bend-table-dim">
          <span className="bend-table-dim-label">Thickness</span>
          <span className="bend-table-dim-value">{pattern.thickness} mm</span>
        </div>
      </div>

      {pattern.bends.length > 0 && (
        <div className="bend-table-section">
          <div className="bend-table-section-title">Bends ({pattern.bends.length})</div>
          <table className="bend-table">
            <thead>
              <tr>
                <th>Edge</th>
                <th>Angle</th>
                <th>BA</th>
                <th>Dev.</th>
              </tr>
            </thead>
            <tbody>
              {pattern.bends.map((b) => (
                <tr key={b.flangeId}>
                  <td>{b.edge}</td>
                  <td>{b.angle}°</td>
                  <td>{b.bendAllowance}</td>
                  <td>{b.developedLength}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
